import {
  creditProductCodes,
  DetailsRowData,
  FileRowData,
  Insurance,
  MyFile,
  RecordsColumnsEnum
} from './records.component.model';
import { TranslateService } from '@ngx-translate/core';
import {
  ActionCode,
  cancellationAvailableList,
  deliveryAvailableList,
  documentsAvailableList,
  duplicationAvailableList,
  fileHistoryAvailableList,
  messagingAvailableList,
  signatureAvailableList
} from '../contract-action/contract-action.model';
import { KeyValueLabel, NoValue, TableAction, TableRow } from '@tribeauto/ngx-mobilite-material';
import { ActionButtonEnum } from '../contract-action/action-list.model';
import { formatCurrency, formatDate, formatNumber } from '@angular/common';
import { getCorrectInsuranceLabel } from '../../layout/recap-card/recap-card.mapper';
import { InsuranceResponse } from '../contract-action/contract-response.model';
import { getRvDerogationStatusTranslation } from '../../helpers/rv-derogation.helper';
import { UserTablePreferences } from '../preferences/preferences.model';

export const actionConfigList: { availableList: ActionCode[]; actionButton: ActionButtonEnum }[] = [
  { availableList: messagingAvailableList, actionButton: ActionButtonEnum.MESSAGING },
  { availableList: documentsAvailableList, actionButton: ActionButtonEnum.DOCUMENTS },
  { availableList: signatureAvailableList, actionButton: ActionButtonEnum.SIGNATURE },
  { availableList: fileHistoryAvailableList, actionButton: ActionButtonEnum.FILEHISTORY },
  { availableList: deliveryAvailableList, actionButton: ActionButtonEnum.DELIVERY },
  { availableList: duplicationAvailableList, actionButton: ActionButtonEnum.DUPLICATION },
  { availableList: cancellationAvailableList, actionButton: ActionButtonEnum.CANCELLATION }
];

export const fileToRowData = (file: MyFile, translateService: TranslateService, locale: string): FileRowData => {
  return {
    creationDate: formatDate(file.creationDate, 'dd/MM/yyyy', locale),
    cckNumber: file.cckNumber,
    unreadMessages: file.unreadMessages,
    prospectName: file.prospectName,
    siclidProductCode: file.siclidProductCode
      ? translateService.instant(`application.records.product.${file.siclidProductCode.toLowerCase()}`)
      : '',
    insurances: file.insurances?.length || 0,
    statusSigned: file.signStatus ? translateService.instant(`application.records.table.sign.${file.signStatus}`) : '',
    subStatus: file.subStatus,
    rvDerogationStatus: file.rvDerogationStatus
      ? getRvDerogationStatusTranslation(file.rvDerogationStatus, translateService)
      : '',
    brand: file.vehicle.brand,
    duration: file.duration,
    instalmentAmountWithInsurances: file.installmentAmountWithInsurances
      ? formatNumber(Number(file.installmentAmountWithInsurances), locale, '0.2')
      : '',
    category: getCategoryList(file.insurances),
    pricingLabel: file.pricingLabel,
    residualValueAmount: file.residualValueAmount ? formatNumber(Number(file.residualValueAmount), locale, '0.2') : '',
    webStoreId: file.pointOfSale.webStoreId ?? '',
    webStoreName: file.pointOfSale.webStoreName,
    userLogin: file.userLogin,
    vehicleDeliveryDate: file.vehicleDeliveryDate ? formatDate(file.vehicleDeliveryDate, 'dd/MM/yyyy', locale) : '',
    overdraftAmount: file.overdraftAmount ? formatNumber(Number(file.overdraftAmount), locale, '0.2') : '',
    instalmentAmountWithoutInsurances: file.instalmentAmountWithoutInsurances
      ? formatNumber(Number(file.instalmentAmountWithoutInsurances), locale, '0.2')
      : '',
    used: translateService.instant(`application.records.used.${file.vehicle.used}`),
    model: file.vehicle.model,
    version: file.vehicle.version,
    residualValuePercent: file.residualValuePercent
      ? formatNumber(Number(file.residualValuePercent), locale, '0.2')
      : '',
    type: getSignatureType(file?.signature?.type, translateService),
    pfStatusSigned: getTranslationSigned(file.signature?.pfStatusSigned, translateService),
    deliverystatusSigned: getTranslationSigned(file.signature?.deliverystatusSigned, translateService),
    actions: file.actions,
    details: getDetailsForRowData(file, translateService, locale)
  };
};

export const rowDataToTableRow = (
  rowData: FileRowData,
  translateService: TranslateService,
  userRecordsPreferences: UserTablePreferences
): TableRow => {
  return {
    tableData: Object.keys(rowData)
      .filter(
        key =>
          ![
            RecordsColumnsEnum.actions.toString(),
            RecordsColumnsEnum.unreadMessages.toString(),
            RecordsColumnsEnum.details.toString()
          ].includes(key)
      )
      .filter(key => userRecordsPreferences.columnsDisplayed.includes(key))
      .sort((a, b) => userRecordsPreferences.columnsOrder[a] - userRecordsPreferences.columnsOrder[b])
      .map(key => ({
        key: translateService.instant(`application.records.table.columns.${key}`),
        value: String(rowData[key as keyof FileRowData]),
        iconName: key === RecordsColumnsEnum.insurances ? 'umbrella' : undefined,
        unit: getUnit(key, rowData, translateService),
        isClickable: key === RecordsColumnsEnum.cckNumber,
        isLoading: [RecordsColumnsEnum.userLogin.toString(), RecordsColumnsEnum.subStatus.toString()].includes(key)
      })),
    actions: getActionsAvailable(rowData.actions, translateService),
    description: translateService.instant('application.records.table.details.title'),
    details: getDetailsRowData(rowData.details, translateService),
    unreadMessages: rowData.unreadMessages
  };
};

function addAction(
  availableList: string[],
  actionList: string[],
  tableActionList: TableAction[],
  actionButton: string,
  translateService: TranslateService
): void {
  if (availableList.some(action => actionList.includes(action))) {
    tableActionList.push({
      action: actionButton.toLowerCase(),
      label: translateService.instant(`application.records.action-list.labels.${actionButton.toLowerCase()}`)
    });
  }
}

const getSignatureType = (type: number | NoValue, translateService: TranslateService): string => {
  if (type) {
    switch (type) {
      case 1:
        return translateService.instant('application.records.table.type.paper');
      case 2:
      case 3:
      case 6:
        return translateService.instant('application.records.table.type.face-to-face-demat');
      case 4:
      case 7:
        return translateService.instant('application.records.table.type.remote-demat');
      case 5:
      case 8:
        return translateService.instant('application.records.table.type.remote-sale');
      default:
        return '';
    }
  }
  return '';
};

const getCategoryList = (insurances: Insurance[]): string => {
  if (insurances) {
    const sortedInsurances = [...insurances].sort((a, b) => a.category.localeCompare(b.category));
    return sortedInsurances.map(insurance => insurance.category).join(', ');
  }
  return '';
};

const getUnit = (key: string, rowData: FileRowData, translateService: TranslateService): string | undefined => {
  const monetaryColumns: string[] = [
    RecordsColumnsEnum.overdraftAmount,
    RecordsColumnsEnum.instalmentAmountWithInsurances,
    RecordsColumnsEnum.residualValueAmount,
    RecordsColumnsEnum.instalmentAmountWithoutInsurances
  ];
  if (String(rowData[key as keyof FileRowData])) {
    if (monetaryColumns.includes(key)) {
      return translateService.instant('application.global.symbols.euro');
    }
    if (key === RecordsColumnsEnum.residualValuePercent) {
      return translateService.instant('application.global.symbols.percent');
    }
    if (key === RecordsColumnsEnum.duration) {
      return translateService.instant('application.global.symbols.months');
    }
  }
  return undefined;
};

export const getActionsAvailable = (
  actionList: string[] | NoValue,
  translateService: TranslateService
): TableAction[] => {
  const tableActionList: TableAction[] = [];
  if (actionList) {
    for (const actionConfig of actionConfigList) {
      addAction(actionConfig.availableList, actionList, tableActionList, actionConfig.actionButton, translateService);
    }
  }
  return tableActionList;
};

const getDetailsForRowData = (file: MyFile, translateService: TranslateService, locale: string): DetailsRowData => {
  const subscribedInsurances = file.insurances
    ?.map(insurance =>
      translateService.instant(
        getCorrectInsuranceLabel({ category: insurance.category, code: insurance.code } as InsuranceResponse) ??
          insurance.category
      )
    )
    .join(', ');
  return {
    brand: file.vehicle.brand,
    duration: translateService.instant('application.records.table.details.duration-months', {
      duration: file.duration
    }),
    installmentWithInsurances: formatCurrency(file.installmentAmountWithInsurances, locale, '€'),
    subscribedInsurances,
    pricingLabel: file.pricingLabel,
    ...(creditProductCodes.includes(file.siclidProductCode) || !file.residualValueAmount
      ? {}
      : {
          residualValue: formatCurrency(file.residualValueAmount, locale, '€')
        })
  };
};

const getDetailsRowData = (rowDetails: DetailsRowData, translateService: TranslateService): KeyValueLabel[] => {
  return Object.keys(rowDetails).map(key => ({
    key,
    value: translateService.instant(`application.records.table.details.${key}`),
    label: String(rowDetails[key as keyof DetailsRowData] ?? '')
  }));
};

const getTranslationSigned = (signed: boolean | NoValue, translateService: TranslateService): string => {
  return signed ? translateService.instant('application.records.table.sign.signed') : '';
};
